import React, { useContext, useState } from 'react'
import { IBreadcrumb } from '@notino/react-styleguide'

// components
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb'
import Reservation from './components/Reservation/Reservation'
import CancelReservationModal from './components/CancelReservationModal/CancelReservationModal'
import OverlayLoading from '../../atoms/OverlayLoading/OverlayLoading'
// import ReservationBookingModal from '../../components/ReservationBookingModal/ReservationBookingModal'
import MyReservationPageHead from './components/MyReservationPageHead/MyReservationPageHead'

// types
import {
	ConfigResponse,
	// SalonServicesResponse,
	ReservationResponse,
	MyReservationPageQueryType,
	// ReservationBookingModalInitData,
	CmsMyReservationPageData,
	PageProps
} from '../../types/types'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { AppContext } from '../../utils/appProvider'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './MyReservationPageStyles'

type RouteProps = {
	reservationID: string
}

type MyReservationPageData = {
	reservationData: ReservationResponse
	configData: ConfigResponse
	myReservationPageCmsData: CmsMyReservationPageData
}

type MyReservationPageProps = PageProps<MyReservationPageData, MyReservationPageQueryType, RouteProps>

const MyReservationPage = (props: MyReservationPageProps) => {
	const { reservationID /* , salonServicesData */, configData, reservationData, setPageData, myReservationPageCmsData, isFetching } = props
	const { image: promoBanner } = myReservationPageCmsData.reservationDetailBannerData

	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)
	const token = props.query.t

	// NOTE - temporary disabled to book again
	// const [reservationBookingModalInitData, setReservationBookingModalInitData] = useState<ReservationBookingModalInitData | null>(null)
	const [cancelReservationModalData, setCancelReservationModalData] = useState<{ reservationID: string } | null>()

	const fetchReservationData = async () => {
		const config = {
			headers: {
				Authorization: `Bearer ${token}`
			}
		}

		setPageData({ dataKey: 'reservationData', data: apiBrowser.b2c.getReservationData(reservationID, undefined, config) })
	}

	const onCancelReservation = () => {
		setCancelReservationModalData({ reservationID })
	}

	const onCancelReservationSubmitSuccess = () => {
		fetchReservationData()
		setCancelReservationModalData(null)
	}

	/* const onBookAgain = (reservation: NonNullable<ReservationResponse['reservation']>) => {
		const reservationModalInitData: ReservationBookingModalInitData = {
			initIndustryID: null,
			initServiceID: reservation.service?.id ?? null,
			initAvResTimeSlotDayPart: null,
			initServiceCategoryParameterID:
				reservation.service?.useCategoryParameter && reservation.service?.serviceCategoryParameter?.id
					? reservation.service.serviceCategoryParameter.id
					: null,
			parameterSelectionData: null,
			employeeAndDateSelectionData: {
				employeeID: reservation.employee.id
			},
			bookingConfirmationData: {
				email: reservation.customer?.email,
				isVisitor: reservation.customer?.isVisitor,
				visitor:
					reservation.customer?.isVisitor && reservation.customer.firstName && reservation.customer.lastName
						? { firstName: reservation.customer.firstName, lastName: reservation.customer.lastName }
						: undefined,
				phone:
					reservation.customer?.phone && reservation.customer?.phonePrefixCountryCode
						? {
								phoneNumber: reservation.customer?.phone,
								phonePrefixCountryCode: reservation.customer?.phonePrefixCountryCode
							}
						: undefined
			}
		}
		setReservationBookingModalInitData(reservationModalInitData)
	} */

	const breadcrumbItems: IBreadcrumb[] = [
		/* { link: PAGE_LINKS['/salons/my-reservations'](locale), name: messages['My reservations'] */ { name: messages['Reservation detail'], link: '' }
	]

	// NOTE: mobile banner doesn't exist for now
	const promoBannerImage = <SC.PromoBannerImage src={promoBanner.desktop} alt={promoBanner.alt} />

	return (
		<>
			<MyReservationPageHead reservation={reservationData.reservation} />

			<SC.PageContainer>
				<Breadcrumb breadcrumbItems={breadcrumbItems} hasSalonsBreadcrumb />
				<SC.MainContent>
					<SC.ReservationContainer>
						<OverlayLoading isLoading={isFetching}>
							<Reservation
								reservation={reservationData.reservation}
								// salonServicesData={salonServicesData}
								onCancelReservation={onCancelReservation}
								// onBookAgain={onBookAgain}
							/>
						</OverlayLoading>
					</SC.ReservationContainer>
					<SC.SidebarContainer>
						{promoBanner.link ? (
							<SC.PromoBannerLink href={promoBanner.link} target={'blank'} rel={'noopener noreferrer'}>
								{promoBannerImage}
							</SC.PromoBannerLink>
						) : (
							promoBannerImage
						)}
					</SC.SidebarContainer>
				</SC.MainContent>
			</SC.PageContainer>
			{/** // NOTE - temporary disabled to book again  */}
			{/* reservationBookingModalInitData && salonServicesData && (
				<ReservationBookingModal
					salonData={{
						phones: reservationData.reservation.salon.phones,
						id: reservationData.reservation.salon.id,
						availableReservationSystem: true,
						images: reservationData.reservation.salon.images
					}}
					isOpen
					initData={reservationBookingModalInitData}
					onClose={() => setReservationBookingModalInitData(null)}
					salonServicesData={salonServicesData}
					configData={configData}
				/>
			) */}
			{cancelReservationModalData && (
				<CancelReservationModal
					isOpen
					token={token}
					reservationID={cancelReservationModalData.reservationID}
					reservationCancellationQuickFeedbackAnswers={configData.reservationCancellationQuickFeedbackAnswers}
					onClose={() => setCancelReservationModalData(null)}
					onSubmitSuccess={onCancelReservationSubmitSuccess}
				/>
			)}
		</>
	)
}

// page container
const MyReservationPageContainer = createPageContainer<MyReservationPageData, MyReservationPageQueryType, RouteProps>({
	identifier: 'MyReservationPage',
	PageComponent: MyReservationPage,
	initDefaultData: async ({ api, reservationID, query, isAuthorized }) => {
		const config = isAuthorized
			? undefined
			: {
					headers: {
						Authorization: `Bearer ${query.t}`
					}
				}
		const [reservationData, configData, myReservationPageCmsData] = await Promise.all([
			api.b2c.getReservationData(reservationID, undefined, config),
			api.b2c.getConfigData(),
			api.cms.getMyReservationPageCmsData()
		])
		return {
			reservationData,
			configData,
			myReservationPageCmsData
		}
	}
})

export default MyReservationPageContainer
